/**
 * The Goal Templates contract — one flat router, `goalTemplateContract` (`/goal-templates`). An HR
 * admin authors a reusable template (name, KRA category, default weight, weighted key results); an
 * employee/manager creating a goal can start from one, whose CURRENT content is SNAPSHOTTED onto the
 * new `Goal`/`GoalKeyResult` rows at creation time (`goal.contract.ts`'s `createGoalBodySchema.
 * templateId`, handled in `apps/api/src/routes/goal.ts`'s `createGoal`) — never a live reference, so
 * editing a template afterward must never change a goal already created from it. See
 * `packages/db/prisma/schema.prisma`'s `GoalTemplate`/`GoalTemplateKeyResult`/`Goal.sourceTemplateId`
 * comments for the full snapshot reasoning.
 *
 * PERMISSIONS ARE REUSED, NOT NEW: `goal:manage` gates every write here (the same permission that
 * already gates `createObjective`/`updateObjective`/`archiveObjective`), `goal:read` gates reads —
 * same shape as `goal:create` for goal creation itself, which stays `routeMeta(null)` in
 * `goal.contract.ts` since applying a template still goes through the ordinary `createGoal` scope
 * rules (self, or a manager's report).
 *
 * ARCHIVE-ONLY, NO HARD DELETE — same discipline as `ReviewTemplate`/`Competency`
 * (`competency.contract.ts`): a template already used by past goals (via `sourceTemplateId`) must
 * not disappear from under that history. `archiveGoalTemplate` only flips `isActive: false`, and
 * `createGoal` refuses to apply an inactive template (422, same shape as its existing
 * "Objective is not active" check).
 *
 * NO SUM-TO-100 RULE ON `keyResults[].weightPercent` — matching `GoalKeyResult`'s own weights,
 * which `goal.ts`'s `createGoal` has never enforced a total for (see that file's header on the
 * evenShare/parent-KeyResult split). A template mirrors the shape it snapshots onto.
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { GOAL_MEASURE_TYPES } from '../enums.js';
import {
  currencySchema,
  errorBodySchema,
  instantSchema,
  measureValueSchema,
  orgIdSchema,
  paginatedSchema,
  paginationQuerySchema,
  percentSchema,
  uuidSchema,
} from '../schemas/common.js';
import { routeMeta } from './meta.js';

const c = initContract();

// ---------------------------------------------------------------------------
// DTOs
// ---------------------------------------------------------------------------

const goalTemplateMeasureTypeSchema = z.enum(GOAL_MEASURE_TYPES);

export const goalTemplateKeyResultSchema = z.object({
  id: uuidSchema,
  title: z.string(),
  measureType: goalTemplateMeasureTypeSchema,
  targetValue: measureValueSchema,
  /** Required exactly when `measureType === 'currency'` (CLAUDE.md invariant 2) — enforced at the
   * route, same pairing as `keyResultSchema`/`goalKeyResultSchema` in goal.contract.ts. */
  currency: currencySchema.nullable(),
  weightPercent: percentSchema,
  sortOrder: z.number().int().default(0),
});
export type GoalTemplateKeyResult = z.infer<typeof goalTemplateKeyResultSchema>;

export const goalTemplateSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  name: z.string(),
  description: z.string().nullable(),
  kraCategory: z.string().nullable(),
  /** What a goal created from this template gets for `weightPercent` when the caller doesn't
   * override it — snapshotted at creation, never read live afterward. */
  defaultWeightPercent: percentSchema,
  isActive: z.boolean(),
  keyResults: z.array(goalTemplateKeyResultSchema),
  createdAt: instantSchema,
  updatedAt: instantSchema,
});
export type GoalTemplate = z.infer<typeof goalTemplateSchema>;

// ---------------------------------------------------------------------------
// Requests
// ---------------------------------------------------------------------------

export const listGoalTemplatesQuerySchema = paginationQuerySchema.extend({
  isActive: z.coerce.boolean().optional(),
});

export const goalTemplateKeyResultInputSchema = z.object({
  title: z.string().trim().min(1).max(200),
  measureType: goalTemplateMeasureTypeSchema,
  targetValue: measureValueSchema,
  currency: currencySchema.optional(),
  weightPercent: percentSchema,
  sortOrder: z.number().int().optional(),
});
export type GoalTemplateKeyResultInput = z.infer<typeof goalTemplateKeyResultInputSchema>;

export const createGoalTemplateBodySchema = z.object({
  name: z.string().trim().min(1).max(200),
  description: z.string().trim().max(2000).optional(),
  kraCategory: z.string().trim().max(100).optional(),
  defaultWeightPercent: percentSchema,
  keyResults: z.array(goalTemplateKeyResultInputSchema).max(20).optional(),
});
export type CreateGoalTemplateBody = z.infer<typeof createGoalTemplateBodySchema>;

/** Every field optional — a partial update. `keyResults`, when given, REPLACES the whole set (same
 * "full running list at once" reasoning as `setTemplateCompetencies` in competency.contract.ts) —
 * there is no separate endpoint for it since a template's key result set is small and edited as a
 * unit in the admin form. Omit it to leave the existing set untouched. */
export const updateGoalTemplateBodySchema = z.object({
  name: z.string().trim().min(1).max(200).optional(),
  description: z.string().trim().max(2000).optional(),
  kraCategory: z.string().trim().max(100).optional(),
  defaultWeightPercent: percentSchema.optional(),
  isActive: z.boolean().optional(),
  keyResults: z.array(goalTemplateKeyResultInputSchema).max(20).optional(),
});
export type UpdateGoalTemplateBody = z.infer<typeof updateGoalTemplateBodySchema>;

// ---------------------------------------------------------------------------
// Contract — /goal-templates
// ---------------------------------------------------------------------------

export const goalTemplateContract = c.router(
  {
    listGoalTemplates: {
      method: 'GET',
      path: '/',
      query: listGoalTemplatesQuerySchema,
      responses: {
        200: paginatedSchema(goalTemplateSchema),
        401: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'List goal templates',
      metadata: routeMeta('goal:read'),
    },

    getGoalTemplate: {
      method: 'GET',
      path: '/:templateId',
      responses: {
        200: goalTemplateSchema,
        401: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Get one goal template with its weighted key results',
      metadata: routeMeta('goal:read'),
    },

    createGoalTemplate: {
      method: 'POST',
      path: '/',
      body: createGoalTemplateBodySchema,
      responses: {
        201: goalTemplateSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        422: errorBodySchema,
      },
      summary: 'Create a goal template — goal:manage',
      metadata: routeMeta('goal:manage'),
    },

    updateGoalTemplate: {
      method: 'PATCH',
      path: '/:templateId',
      body: updateGoalTemplateBodySchema,
      responses: {
        200: goalTemplateSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        422: errorBodySchema,
      },
      summary:
        'Update a goal template, optionally replacing its whole key-result set — goal:manage',
      metadata: routeMeta('goal:manage'),
    },

    archiveGoalTemplate: {
      method: 'POST',
      path: '/:templateId/archive',
      body: z.object({}),
      responses: {
        200: goalTemplateSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Archive (deactivate) a goal template — goal:manage',
      metadata: routeMeta('goal:manage'),
    },
  },
  { pathPrefix: '/goal-templates' },
);
